import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subscriptionApi } from '../../services/subscriptionApi';
import './Pricing.css';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId');
  const [plan, setPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const paypalContainerRef = useRef(null);
  const cardFieldsRef = useRef(null);
  const sdkLoadedRef = useRef(false);

  useEffect(() => {
    if (!planId) {
      navigate('/user-home/pricing');
      return;
    }
    const load = async () => {
      try {
        const res = await subscriptionApi.getPlan(planId);
        setPlan(res?.plan || null);
      } catch (e) {
        setError('Failed to load plan details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [planId, navigate]);

  useEffect(() => {
    if (!plan || sdkLoadedRef.current) return;

    const loadPayPalSDK = async () => {
      try {
        const configRes = await subscriptionApi.getPaypalConfig();
        const clientId = configRes?.clientId;
        if (!clientId) {
          setError('PayPal configuration not available');
          return;
        }

        const paypalPlanId = billingCycle === 'yearly'
          ? plan.paypalPlanIdYearly
          : plan.paypalPlanIdMonthly;

        if (!paypalPlanId) {
          setError('This plan is not yet available for purchase. Please contact support.');
          return;
        }

        // Load PayPal JS SDK
        const script = document.createElement('script');
        script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription&components=buttons,card-fields`;
        script.setAttribute('data-sdk-integration-source', 'button-factory');
        script.async = true;

        script.onload = () => {
          sdkLoadedRef.current = true;
          renderPayPalButtons(paypalPlanId);
          renderCardFields(paypalPlanId);
        };

        script.onerror = () => {
          setError('Failed to load PayPal SDK');
        };

        document.body.appendChild(script);

        return () => {
          document.body.removeChild(script);
        };
      } catch (e) {
        setError('Failed to initialize payment');
      }
    };

    loadPayPalSDK();
  }, [plan, billingCycle]);

  const renderPayPalButtons = (paypalPlanId) => {
    if (!window.paypal || !paypalContainerRef.current) return;

    paypalContainerRef.current.innerHTML = '';

    window.paypal.Buttons({
      style: {
        shape: 'rect',
        color: 'gold',
        layout: 'vertical',
        label: 'subscribe',
      },
      createSubscription: (data, actions) => {
        return actions.subscription.create({
          plan_id: paypalPlanId,
        });
      },
      onApprove: async (data) => {
        await handleSubscriptionApproved(data.subscriptionID, 'paypal');
      },
      onError: (err) => {
        console.error('PayPal button error:', err);
        setError('Payment failed. Please try again.');
      },
    }).render(paypalContainerRef.current);
  };

  const renderCardFields = (paypalPlanId) => {
    if (!window.paypal?.CardFields) return;

    const cardFieldsContainer = document.getElementById('card-fields-container');
    if (!cardFieldsContainer) return;

    cardFieldsContainer.innerHTML = '';

    const cardFields = window.paypal.CardFields({
      createSubscription: (data, actions) => {
        return actions.subscription.create({
          plan_id: paypalPlanId,
        });
      },
      onApprove: async (data) => {
        await handleSubscriptionApproved(data.subscriptionID, 'card', data);
      },
      onError: (err) => {
        console.error('Card fields error:', err);
        setError('Card payment failed. Please check your card details.');
      },
    });

    if (cardFields.isEligible()) {
      const numberField = cardFields.NumberField();
      const expiryField = cardFields.ExpiryField();
      const cvvField = cardFields.CVVField();

      numberField.render('#card-number-field');
      expiryField.render('#card-expiry-field');
      cvvField.render('#card-cvv-field');

      cardFieldsRef.current = cardFields;
    }
  };

  const handleSubscriptionApproved = async (subscriptionID, paymentMethod, cardData) => {
    setActivating(true);
    setError(null);
    try {
      const result = await subscriptionApi.activate({
        paypalSubscriptionId: subscriptionID,
        planId,
        billingCycle,
        paymentMethod,
        cardLast4: cardData?.cardLast4 || null,
      });

      if (result?.success) {
        setSuccess(true);
        setTimeout(() => navigate('/user-home/subscription'), 2000);
      } else {
        setError(result?.error || 'Failed to activate subscription');
      }
    } catch (e) {
      setError('Failed to activate subscription. Please contact support.');
    } finally {
      setActivating(false);
    }
  };

  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (!cardFieldsRef.current) return;
    setError(null);
    try {
      await cardFieldsRef.current.submit();
    } catch (err) {
      setError('Card payment failed. Please check your details.');
    }
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="pricing-loading">Loading checkout...</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="checkout-page">
        <div className="checkout-success">
          <div className="success-icon">✓</div>
          <h2>Subscription Activated!</h2>
          <p>Welcome to {plan?.planName}. Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-summary">
          <h2>Order Summary</h2>
          <div className="summary-plan">
            <span className="summary-plan-name">{plan?.planName}</span>
            <span className="summary-plan-price">
              ${((billingCycle === 'yearly' ? plan?.yearlyPriceCents : plan?.monthlyPriceCents) / 100).toFixed(2)}
              /{billingCycle === 'yearly' ? 'year' : 'month'}
            </span>
          </div>
          <div className="billing-toggle">
            <button
              className={billingCycle === 'monthly' ? 'active' : ''}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={billingCycle === 'yearly' ? 'active' : ''}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly (save {plan?.yearlyPriceCents ? Math.round((1 - plan.yearlyPriceCents / (plan.monthlyPriceCents * 12)) * 100) : 0}%)
            </button>
          </div>
          <ul className="summary-features">
            <li>{plan?.includedQueries?.toLocaleString()} queries/month</li>
            <li>30-day money-back guarantee</li>
            <li>Cancel anytime</li>
          </ul>
        </div>

        <div className="checkout-payment">
          <h2>Payment Method</h2>
          {error && <div className="checkout-error">{error}</div>}
          {activating && <div className="checkout-activating">Activating your subscription...</div>}

          {/* PayPal Button */}
          <div className="paypal-section">
            <div ref={paypalContainerRef} id="paypal-button-container"></div>
          </div>

          <div className="payment-divider">
            <span>or pay with card</span>
          </div>

          {/* Card Fields */}
          <form onSubmit={handleCardSubmit} className="card-form">
            <div id="card-fields-container">
              <div className="card-field-group">
                <label>Card Number</label>
                <div id="card-number-field" className="card-field"></div>
              </div>
              <div className="card-field-row">
                <div className="card-field-group">
                  <label>Expiry</label>
                  <div id="card-expiry-field" className="card-field"></div>
                </div>
                <div className="card-field-group">
                  <label>CVV</label>
                  <div id="card-cvv-field" className="card-field"></div>
                </div>
              </div>
            </div>
            <button type="submit" className="card-submit-btn" disabled={activating}>
              {activating ? 'Processing...' : `Subscribe — $${((billingCycle === 'yearly' ? plan?.yearlyPriceCents : plan?.monthlyPriceCents) / 100).toFixed(2)}/${billingCycle === 'yearly' ? 'yr' : 'mo'}`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
